import { useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Heart, Minus, Plus, Check } from "lucide-react";

import Seo, { breadcrumbLd, productLd } from "@/components/seo/Seo";
import { fetchProduct } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useWishlist } from "@/contexts/WishlistContext";

import { Rating } from "@/components/ui/rating";
import ReviewsBlock from "@/components/marketing/ReviewsBlock";
import RecipeCard from "@/components/marketing/RecipeCard";
import BottleArt from "@/components/visual/BottleArt";
import { BRAND, LAB_REPORT, PURITY_REPORT } from "@/content/brand";
import { HOME_FAQS } from "@/content/faqs";
import { realGallery } from "@/lib/productImage";
import { cn } from "@/lib/utils";
import { trackViewItem, track, productToItems } from "@/lib/analytics";
import SlidingNumber from "@/components/motion/SlidingNumber";
import AddToBasket from "@/components/shop/AddToBasket";
import Offers from "@/components/shop/Offers";
import AskWhatsApp from "@/components/shop/AskWhatsApp";
import { useCatalog, bundleSaving, pricePerLitre } from "@/lib/shop";
import { LayoutGroup, motion } from "framer-motion";

const PROMISES = [
  "One ingredient: coconut. Nothing added, nothing blended.",
  "Pressed cold. Never refined, bleached or deodorised.",
  "Grown on our family farms in Tiptur and coastal Karnataka.",
  "Latest lab sample met the FSSAI standard for coconut oil on all 6 purity tests.",
  `Every batch tested at an independent food testing lab (${BRAND.batchesTested} so far).`,
];

const WHY = [
  { title: "Only coconut", body: "Pure oil is simple: one ingredient. No palm or mineral oil to stretch it, no colour or aroma added to fake it." },
  { title: "Only cold pressed", body: "Pressed from dried copra with the temperature controlled so no heat is added, then filtered. It never goes through the refining that strips aroma and taste." },
  { title: "Only our coconuts", body: "We grow what we press, on our farms in Tiptur and along the coast from Gokarna to Shirali. That's why our batches are small." },
];

const USES = [
  { title: "Everyday cooking", body: "Tadka, poriyal, thoran, dosa, appam and baking, at medium heat.", to: "/cooking-with-coconut-oil" },
  { title: "Hair champi", body: "Warm a spoonful, massage into the scalp, wash after an hour or leave overnight.", to: "/coconut-oil-for-hair" },
  { title: "Dry skin, heels and lips", body: "A few drops on damp skin after a bath.", to: "/coconut-oil-for-skin" },
  { title: "Baby massage", body: "Patch test first, then a gentle daily massage.", to: "/coconut-oil-for-baby-massage" },
  { title: "Oil pulling", body: "A spoonful swished for ten minutes before brushing.", to: "/oil-pulling-with-coconut-oil" },
];

/** Taxes note, bundle saving against singles, and price per litre. */
function PriceNotes({ variant }) {
  const catalog = useCatalog();
  if (!variant) return null;
  const saving = bundleSaving(variant, catalog);
  const ppl = pricePerLitre(variant);
  return (
    <div className="mt-1 space-y-0.5" data-testid="pdp-price-notes">
      {saving && <p className="font-body text-[14px] font-semibold text-brand-gold">You save ₹{saving.saving} against buying {variant.size.split("×")[0].trim()} single jars ({saving.pct}%)</p>}
      <p className="font-body text-[13px] text-ink-muted">Inclusive of all taxes{ppl ? `. Works out to ₹${ppl} a litre` : ""}.</p>
    </div>
  );
}

/** "2 × 1 L" → price per jar, rounded; null for single jars. */
function perBottle(v) {
  const m = /^(\d+)\s*[×x]/.exec(v?.size || "");
  const n = m ? Number(m[1]) : 0;
  return n > 1 ? Math.round(v.price / n) : null;
}

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [variant, setVariant] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const { user } = useAuth();
  const { has: wishHas, add: wishAdd, remove: wishRemove } = useWishlist();

  const loadProduct = () => fetchProduct(slug).then((p) => {
    setProduct(p);
    setVariant((curr) => curr || p.variants?.find((v) => v.size.replace(/\s/g, "") === "500ml") || p.variants?.[0]);
  }).catch(() => setProduct(false));

  useEffect(() => {
    setActiveImg(0); setQty(1); setVariant(null);
    loadProduct();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // view_item once per product (not per variant click)
  const productKey = product ? product.slug : null;
  useEffect(() => {
    if (product && variant) trackViewItem(product, variant);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productKey]);

  // Mobile buy bar: visible whenever the main Add to basket button is off screen.
  const buyRowRef = useRef(null);
  const hasProduct = !!product;
  useEffect(() => {
    const el = buyRowRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(([e]) => setShowStickyBar(!e.isIntersecting), { rootMargin: "0px 0px -60px 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, [hasProduct]);

  if (product === false) {
    return (
      <div className="max-w-3xl mx-auto py-32 px-6 text-center">
        <h1 className="text-h2 text-brand-ink">We couldn't find that oil.</h1>
        <Link to="/products" className="mt-6 inline-flex rounded-control bg-brand-gold px-6 py-3 font-body text-white">Back to the shop</Link>
      </div>
    );
  }
  if (!product) return <div className="py-40 text-center font-body text-ink-muted">Loading</div>;

  const gallery = realGallery(product);
  const discount = variant && variant.mrp > variant.price
    ? Math.round(((variant.mrp - variant.price) / variant.mrp) * 100)
    : 0;

  const handleWishlist = async () => {
    if (!variant) return;
    if (!user) {
      // guest: redirect to login with intent
      navigate("/login", { state: { from: `/products/${slug}`, intent: "wishlist" } });
      return;
    }
    if (wishHas(variant.id)) {
      await wishRemove(variant.id);
    } else {
      const res = await wishAdd(variant.id);
      if (res?.ok) {
        const items = productToItems(product, variant);
        track("add_to_wishlist", { currency: "INR", value: variant.price, items });
      }
    }
  };
  const wishlisted = variant ? wishHas(variant.id) : false;

  return (
    <>
      <Seo
        title={`${product.name}, Cold Pressed`}
        description={`${product.short_desc || "Karijeeva cold pressed coconut oil."} One ingredient, grown on our family farms in Karnataka, every batch lab tested. FSSAI ${BRAND.fssai}.`.slice(0, 300)}
        image={gallery[0]}
        type="product"
        jsonLd={[
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Shop", path: "/products" },
            { name: product.name, path: `/products/${slug}` },
          ]),
          productLd({ ...product, gallery }, product.price_range?.min, product.price_range?.max),
        ]}
      />

      {/* Buy box */}
      <section className="bg-brand-parchment" data-testid="pdp-main">
        <div className="max-w-6xl mx-auto px-6 pt-8 pb-20 grid lg:grid-cols-2 gap-12 lg:gap-16">
          <nav aria-label="Breadcrumb" className="lg:col-span-2 font-body text-[13px] text-ink-muted">
            <Link to="/" className="hover:underline">Home</Link>
            <span className="mx-2" aria-hidden="true">/</span>
            <Link to="/products" className="hover:underline">Shop</Link>
            <span className="mx-2" aria-hidden="true">/</span>
            <span className="text-brand-ink">{product.name}</span>
          </nav>

          <div className="lg:sticky lg:top-20 self-start" data-testid="pdp-gallery">
            <div className="aspect-square rounded-3xl bg-brand-obsidian flex items-center justify-center overflow-hidden">
              {gallery.length ? (
                <img src={gallery[activeImg]} alt={`${product.name}, view ${activeImg + 1}`} className="w-full h-full object-cover" />
              ) : (
                <BottleArt product={product} />
              )}
            </div>
            {gallery.length > 1 && (
              <div className="mt-3 grid grid-cols-5 gap-2">
                {gallery.map((src, i) => (
                  <button key={i} onClick={() => setActiveImg(i)} data-testid={`pdp-thumb-${i}`} aria-label={`Show image ${i + 1}`}
                    className={cn("aspect-square rounded-xl overflow-hidden border-2", i === activeImg ? "border-brand-gold" : "border-transparent opacity-70 hover:opacity-100")}>
                    <img src={src} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            {product.is_new_launch && <p className="font-body text-[14px] font-semibold text-brand-gold">New</p>}
            <h1 className="font-display text-[clamp(2.2rem,4.5vw,3.25rem)] leading-[1.04] tracking-heading text-brand-ink mt-1" data-testid="pdp-name">{product.name}</h1>
            {product.review_count > 0 ? (
              <a href="#reviews" className="mt-3 inline-flex items-center gap-2">
                <Rating value={product.avg_rating} size={16} />
                <span className="font-body text-sm text-ink-muted hover:underline">
                  {product.avg_rating?.toFixed(1)} from {product.review_count.toLocaleString()} {product.review_count === 1 ? "review" : "reviews"}
                </span>
              </a>
            ) : (
              <p className="mt-3 font-body text-sm text-ink-muted">
                <Check className="inline w-4 h-4 -mt-0.5 mr-1 text-brand-gold" aria-hidden="true" />
                Latest batch met the FSSAI standard on all six purity tests. <Link to="/lab-reports" className="underline">See the report</Link>
              </p>
            )}
            <p className="font-body text-[18px] text-brand-husk mt-5 leading-relaxed">{product.short_desc}</p>

            <ul className="mt-6 space-y-2 font-body text-[15px] text-brand-ink">
              {PROMISES.map((t) => (
                <li key={t} className="flex gap-3"><Check className="w-4 h-4 mt-1 text-brand-gold shrink-0" aria-hidden="true" />{t}</li>
              ))}
            </ul>

            <div className="mt-8">
              <p className="font-body text-[14px] font-semibold text-brand-ink">Choose a size</p>
              <LayoutGroup id="pdp-size">
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="pdp-variants">
                {product.variants?.map((v) => (
                  <button
                    key={v.id}
                    onClick={() => setVariant(v)}
                    data-testid={`pdp-variant-${v.size}`}
                    aria-pressed={variant?.id === v.id}
                    className={cn(
                      "relative rounded-2xl border-2 px-4 py-3 text-left transition-colors bg-white",
                      variant?.id === v.id ? "border-transparent" : "border-black/10 hover:border-black/30"
                    )}
                  >
                    {variant?.id === v.id && (
                      <motion.span layoutId="pdp-size-ring" className="absolute -inset-[2px] rounded-2xl border-2 border-brand-gold" transition={{ type: "spring", stiffness: 420, damping: 34 }} />
                    )}
                    <span className="block font-display text-[17px] text-brand-ink">{v.size}</span>
                    <span className="block font-body text-[14px] text-ink-muted">₹{v.price}</span>
                    {perBottle(v) && <span className="block font-body text-[12px] text-ink-muted">₹{perBottle(v)} a jar</span>}
                  </button>
                ))}
              </div>
              </LayoutGroup>
            </div>

            {product.contents?.length > 0 && (
              <div className="mt-6 rounded-2xl bg-white p-5" data-testid="pdp-contents">
                <p className="font-body text-[14px] font-semibold text-brand-ink">What's in the box</p>
                <ul className="mt-2 space-y-1 font-body text-[14px] text-ink-muted">
                  {product.contents.map((c) => <li key={c}>{c}</li>)}
                </ul>
              </div>
            )}

            {variant && (
              <div className="mt-8 flex items-baseline gap-3" data-testid="pdp-price">
                <span className="font-display text-[34px] tracking-heading text-brand-ink"><SlidingNumber value={variant.price * qty} prefix="₹" /></span>
                {qty > 1 && <span className="font-body text-[14px] text-ink-muted">for {qty}</span>}
                {variant.mrp > variant.price && (
                  <>
                    <span className="font-body text-lg text-ink-muted line-through">MRP ₹{variant.mrp}</span>
                    <span className="font-body text-[14px] font-semibold text-brand-gold">Save {discount}%</span>
                  </>
                )}
              </div>
            )}
            <PriceNotes variant={variant} />

            <div ref={buyRowRef} className="mt-6 flex flex-wrap items-center gap-3">
              <div className="inline-flex items-center rounded-control border border-black/15 h-12 bg-white" data-testid="pdp-qty">
                <button onClick={() => setQty((q) => Math.max(1, q - 1))} aria-label="Decrease quantity" className="w-11 h-full flex items-center justify-center text-brand-ink"><Minus className="w-4 h-4" /></button>
                <span className="w-8 text-center font-display text-lg text-brand-ink" aria-live="polite">{qty}</span>
                <button onClick={() => setQty((q) => q + 1)} aria-label="Increase quantity" className="w-11 h-full flex items-center justify-center text-brand-ink"><Plus className="w-4 h-4" /></button>
              </div>
              <AddToBasket product={product} variant={variant} qty={qty} className="flex-1 sm:flex-none whitespace-nowrap px-4 sm:px-8 text-[15px] sm:text-[16px]" testid="pdp-add-to-cart" />
              <button onClick={handleWishlist} data-testid="pdp-wishlist" aria-label={wishlisted ? "Remove from wishlist" : "Save to wishlist"} aria-pressed={wishlisted}
                className="h-12 w-12 rounded-control border border-black/15 bg-white inline-flex items-center justify-center text-brand-ink">
                <Heart className={cn("w-5 h-5", wishlisted && "fill-brand-gold text-brand-gold")} />
              </button>
            </div>

            <AskWhatsApp topic={`${product.name}, ${variant?.size || ""}`.replace(/, $/, "")} className="mt-4" />

            <div className="mt-6"><Offers /></div>

            <dl className="mt-10 grid grid-cols-2 gap-4 border-t border-black/10 pt-8 font-body text-[14px]">
              {[
                ["Dispatch", "Within 24 to 48 hours"],
                ["Delivery", "Across India, 3 to 7 business days"],
                ["Returns", "Unopened jars within 7 days"],
                ["FSSAI licence", BRAND.fssai],
              ].map(([k, v]) => (
                <div key={k}><dt className="text-ink-muted">{k}</dt><dd className="text-brand-ink mt-0.5">{v}</dd></div>
              ))}
            </dl>
          </div>
        </div>
      </section>

      {/* Why this oil */}
      <section className="bg-brand-obsidian text-brand-bone py-24" data-testid="pdp-why">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="font-display text-white text-[clamp(2rem,5vw,3.5rem)] leading-[1.04] tracking-display max-w-3xl">What's in the jar, and what never is.</h2>
          <div className="mt-12 grid md:grid-cols-3 gap-4">
            {WHY.map((w) => (
              <div key={w.title} className="rounded-3xl bg-brand-obsidian-soft p-8">
                <h3 className="font-display text-white text-[22px] tracking-[-0.02em]">{w.title}</h3>
                <p className="mt-3 font-body text-brand-bone/75 leading-relaxed">{w.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Details: how to use, nutrition, ingredients */}
      <section className="bg-brand-parchment py-24" data-testid="pdp-tabs">
        <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-16">
          <div>
            <h2 className="font-display text-[32px] tracking-heading text-brand-ink">How families use it</h2>
            <ul className="mt-6 divide-y divide-black/10 border-y border-black/10">
              {USES.map((u) => (
                <li key={u.title} className="py-5">
                  <Link to={u.to} className="group block">
                    <p className="font-display text-[19px] text-brand-ink group-hover:underline">{u.title}</p>
                    <p className="font-body text-[15px] text-ink-muted mt-1 leading-relaxed">{u.body}</p>
                  </Link>
                </li>
              ))}
            </ul>
            <h2 className="mt-14 font-display text-[24px] tracking-[-0.02em] text-brand-ink">Ingredients</h2>
            <p className="mt-2 font-body text-[16px] text-ink-muted">100% cold pressed coconut oil. Nothing added.</p>
            <h2 className="mt-8 font-display text-[24px] tracking-[-0.02em] text-brand-ink">Storage</h2>
            <p className="mt-2 font-body text-[16px] text-ink-muted leading-relaxed">Keep the cap closed and the jar away from direct sunlight. Below about 24°C the oil turns white and solid; stand the jar in warm water for a few minutes and it turns clear again.</p>
          </div>
          <div>
            <h2 className="font-display text-[32px] tracking-heading text-brand-ink">Nutrition, per 100 g</h2>
            <p className="font-body text-[14px] text-ink-muted mt-1">From our independent lab report {LAB_REPORT.ref}.</p>
            <table className="mt-6 w-full font-body text-[15px]" data-testid="pdp-nutrition">
              <tbody>
                {LAB_REPORT.rows.map((r) => (
                  <tr key={r.param} className="border-b border-black/10">
                    <th scope="row" className="py-3 text-left font-normal text-brand-ink">{r.param}</th>
                    <td className="py-3 text-right text-brand-ink">{r.value} {r.unit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <h2 className="mt-12 font-display text-[24px] tracking-[-0.02em] text-brand-ink">Purity, against the FSSAI standard</h2>
            <p className="font-body text-[14px] text-ink-muted mt-1">Report {PURITY_REPORT.ref}. Met the standard on every test.</p>
            <table className="mt-4 w-full font-body text-[15px]" data-testid="pdp-purity">
              <tbody>
                {PURITY_REPORT.rows.map((r) => (
                  <tr key={r.param} className="border-b border-black/10">
                    <th scope="row" className="py-3 pr-3 text-left font-normal text-brand-ink">{r.param}</th>
                    <td className="py-3 text-right text-brand-ink whitespace-nowrap">{r.value}<span className="block text-[12px] text-ink-muted">limit {r.limit}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2">
              <Link to="/coconut-oil-lab-report-explained" className="font-body text-[15px] text-brand-gold hover:underline">What each test means</Link>
              <Link to="/lab-reports" className="font-body text-[15px] text-brand-gold hover:underline">About our lab testing</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="bg-brand-parchment-soft py-24" id="reviews" data-testid="pdp-reviews">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="font-display text-[32px] tracking-heading text-brand-ink">Reviews</h2>
          <div className="mt-8">
            <ReviewsBlock product={product} user={user} onSubmitted={loadProduct} />
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="bg-brand-parchment py-24" data-testid="pdp-faq">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="font-display text-[32px] tracking-heading text-brand-ink">Before you buy</h2>
          <div className="mt-8 divide-y divide-black/10 border-y border-black/10">
            {HOME_FAQS.map((f) => (
              <details key={f.q} className="group py-5">
                <summary className="cursor-pointer list-none flex items-start justify-between gap-6 font-display text-[18px] text-brand-ink">
                  {f.q}
                  <span className="font-body text-2xl leading-none text-ink-muted transition-transform group-open:rotate-45" aria-hidden="true">+</span>
                </summary>
                <p className="mt-3 font-body text-[16px] leading-relaxed text-ink-muted">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {product.pairs_with_recipes?.length > 0 && (
        <section className="bg-brand-parchment-soft py-24" data-testid="pdp-pairs-with">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="font-display text-[32px] tracking-heading text-brand-ink">Cook something with it</h2>
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {product.pairs_with_recipes.slice(0, 3).map((r) => <RecipeCard key={r.slug} recipe={r} />)}
            </div>
          </div>
        </section>
      )}

      {showStickyBar && (
        <div className="lg:hidden fixed inset-x-0 bottom-0 z-40 bg-white/95 backdrop-blur border-t border-black/10 px-4 py-3 flex items-center gap-3" data-testid="pdp-sticky-bar">
          <div className="flex-1">
            <div className="font-display text-xl text-brand-ink leading-none">₹{variant?.price}</div>
            <div className="font-body text-xs text-ink-muted">{variant?.size} · inclusive of taxes</div>
          </div>
          <AddToBasket product={product} variant={variant} qty={qty} className="h-11 px-6 text-[15px]" testid="pdp-sticky-add" />
        </div>
      )}
    </>
  );
}
